import { CloudArrowUp, CheckCircle, Spinner, WifiSlash } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import { useTranslation } from 'react-i18next';
import { useStorageMode } from '@/hooks/useStorageMode';

export function OfflineSyncIndicator() {
  const { pendingCount, isSyncing, isOnline } = useOfflineSync();
  const { isLocalMode } = useStorageMode();
  const { t } = useTranslation();

  // Only show in cloud mode
  if (isLocalMode) return null;

  // Don't show if nothing pending and online
  if (pendingCount === 0 && isOnline && !isSyncing) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="fixed top-0 left-0 right-0 z-[60]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className={`flex items-center justify-center gap-2 py-2 px-4 text-sm ${
          !isOnline 
            ? 'bg-destructive text-destructive-foreground' 
            : isSyncing 
              ? 'bg-primary text-primary-foreground'
              : pendingCount > 0 
                ? 'bg-warning text-warning-foreground'
                : 'bg-success text-success-foreground'
        }`}>
          {!isOnline ? (
            <>
              <WifiSlash className="w-4 h-4" weight="bold" />
              <span>
                {t('errors.offline')}
                {pendingCount > 0 && ` • ${t('sync.pendingChanges', { count: pendingCount })}`}
              </span>
            </>
          ) : isSyncing ? (
            <>
              <Spinner className="w-4 h-4 animate-spin" />
              <span>{t('sync.syncing')}</span>
            </>
          ) : pendingCount > 0 ? (
            <>
              <CloudArrowUp className="w-4 h-4" />
              <span>{t('sync.pendingChanges', { count: pendingCount })}</span>
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              <span>{t('sync.allSynced')}</span>
            </>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
